/**
 * Die Arten eines Ticket-Eintrags — EINE Wahrheit für Server und App.
 * (Vier bis 24.09.2026; seitdem dazu `rueckmeldung`, siehe unten.)
 *
 * ⚠️⚠️ 14.09.2026 — `system` war deklariert, aber nirgends umgesetzt.
 *
 * Am 06.09. wurde entschieden, dass eine Technik-Diagnose keine Aufgabe ist.
 * Martin, als die Abriss-Meldung in seiner Offen-Liste stand: „Was mache ich
 * mit dieser Info, außer dass ich sie akzeptiere? Das ist etwas für das
 * Protokoll und nicht für mich, oder?"
 *
 * `kind: "system"` wurde daraufhin an der Abriss-Wache gesetzt (server/
 * index.ts, onOrphanFinalized) — aber sowohl der INSERT als auch die
 * Zeilen-Abbildung in memory/tickets.ts ließen still nur "notification" und
 * "thread" durch und machten aus allem anderen "ticket". Die Entscheidung
 * hatte nie eine Wirkung.
 *
 * NACHGEZÄHLT in der Ticket-Tabelle: Bis zum 06.09. liefen diese Meldungen
 * als `notification` („Aufnahme abgerissen — bitte prüfen"). Seit dem 09.09.
 * stehen NEUN davon als normale offene Aufgaben in Martins Liste („Aufnahme
 * war unterbrochen"); acht wurden von Hand geschlossen, eine (#2247) stand
 * beim Schreiben dieser Zeilen noch offen. Genau der Anzeigemüll, gegen den
 * der nächtliche Aufräumer gebaut wurde — nur an ihm vorbei, weil er
 * `kind='notification'` sucht.
 *
 * Die Liste stand vorher fünfmal im Code (zweimal im Store, zweimal in der
 * Ticket-Ansicht, einmal in zuhause.ts), und `system` fehlte in jeder.
 */
export type TicketKind = "ticket" | "notification" | "thread" | "system" | "rueckmeldung";

/*
 * ═══ Rückmeldung (24.09.2026, Ticket 2567, Onboarding-Plan Stufe 0a) ═══
 *
 * Was ein Konto OHNE Betreiber-Recht als Ticket anlegt — über den Knopf
 * (POST /api/tickets), das Werkzeug ticket_create oder eine Bitte in der
 * Tagesaufnahme —, wird als `rueckmeldung` geführt, nicht als `ticket`.
 * Die Umdeutung macht der TicketStore beim Anlegen (memory/tickets.ts).
 *
 * Martins Plan: *„Tickets von Nicht-Betreibern werden als ‚Rückmeldung'
 * geführt und kommen nie in die serielle Warteschlange der
 * Selbstverbesserung. Sonst wären sie ein Einfallstor für eingeschleuste
 * Anweisungen.“* Ein Ticket-Text geht dort wörtlich in den Auftrag einer
 * Claude-Sitzung mit Schreibrecht auf die Arbeitskopie.
 *
 * Eine Rückmeldung
 *   - ist KEINE Aufgabe (KEINE_AUFGABE): keine Entwickler-Knöpfe, nicht in
 *     ticket_list_open, nicht in der Zahl offener Aufgaben;
 *   - geht den Betreiber an (BETRIFFT_BETREIBER): Glocke und „Von deinen
 *     Nutzern“ im Adminbereich, wie ein Ticket bisher;
 *   - ist keine reine Anzeige (NUR_ANZEIGE): der Aufräumer lässt sie stehen,
 *     bis Martin sie gelesen hat;
 *   - darf nie selbst bearbeitet werden (selbstBearbeitbar).
 */

/**
 * Arten, die NICHTS von Martin verlangen — sie zählen nicht als offene
 * Aufgabe und bekommen keine Entwickler-Knöpfe.
 */
export const KEINE_AUFGABE: readonly TicketKind[] = [
  "notification",
  "thread",
  "system",
  "rueckmeldung",
];

/**
 * Arten, die der naechtliche Aufraeumer wegraeumen darf, sobald sie gesehen
 * sein konnten: reine Anzeigen.
 *
 * ⚠️ `thread` gehoert NICHT dazu — ein Gespraechsfaden wartet auf eine
 * Antwort, ihn zu raeumen waere ein falsches Versprechen. `system` schon:
 * eine Technik-Diagnose verlangt nichts und hat ihren Zweck erfuellt, sobald
 * sie einmal dastand.
 */
const NUR_ANZEIGE: readonly TicketKind[] = ["notification", "system"];

/** Darf nach der Stehzeit geraeumt werden? */
export function istNurAnzeige(kind: unknown): boolean {
  return NUR_ANZEIGE.includes(kind as TicketKind);
}

/** Ist das ein Eintrag, der Arbeit an der App bedeutet? */
export function istAufgabe(kind: unknown): boolean {
  return !KEINE_AUFGABE.includes(kind as TicketKind);
}

/**
 * Nur bekannte Arten überleben; alles Unbekannte wird ein gewöhnliches
 * Ticket. ⚠️ Das ist die Stelle, an der `system` vorher verloren ging.
 */
/**
 * Arten, die den Betreiber angehen, wenn sie von einem ANDEREN Konto kommen.
 * (21.09.2026)
 *
 * Martin: *„Kommen jetzt auch die Albert-Wuensche bei uns an, wenn er unten
 * auf den Button klickt?"* — Nachgemessen war die Antwort nein: seit die
 * persoenliche Liste je Konto filtert (pre.3116), erreichte ihn gar nichts
 * mehr von seinen Nutzern.
 *
 * ⚠️ Die Trennung ist nicht „alles oder nichts". Martins Beschwerde am selben
 *    Tag galt Alberts FAEDEN („Albert an die Geschichte seines Vaters
 *    erinnern", „Buch SV PANTIKI") — die gehoeren ihm, nicht dem Betreiber.
 *    Was den Betreiber angeht, ist die Meldung UEBER DAS SYSTEM.
 */
const BETRIFFT_BETREIBER: readonly TicketKind[] = ["ticket", "system", "rueckmeldung"];

export function gehtDenBetreiberAn(kind: unknown): boolean {
  return BETRIFFT_BETREIBER.includes(normKind(kind));
}

export function normKind(k: unknown): TicketKind {
  return k === "notification" || k === "thread" || k === "system" || k === "rueckmeldung"
    ? k
    : "ticket";
}

/**
 * Die Art, unter der ein neues Ticket gespeichert wird. (24.09.2026, Stufe 0a)
 *
 * Aus einem `ticket` eines Kontos ohne Betreiber-Recht wird eine
 * `rueckmeldung`. Alles andere bleibt: Erinnerungen, Fäden und
 * Systemmeldungen legt das System selbst an, ihr Text stammt nicht vom Konto.
 */
/** Der sichtbare Zusatz im Titel eines Tickets, das nach gelesenem Fremdtext entstand (tools/herkunft-merker.ts). */
export const TITEL_ZUSATZ = "[nach Fremdtext] ";

export function artBeimAnlegen(kind: unknown, betreiber: boolean, nachFremdtext = false): TicketKind {
  const k = normKind(kind);
  // 14.10.2026 (Ticket 2875, Folgearbeit): auch ein Ticket, das im Zug mit gelesenem Fremdtext entstand, ist eine Rückmeldung —
  // die Selbstverbesserung nimmt es nie von selbst an (tools/herkunft-merker.ts).
  return k === "ticket" && (!betreiber || nachFremdtext) ? "rueckmeldung" : k;
}

/**
 * Stammt der Text dieses Eintrags von einem Konto ohne Betreiber-Recht?
 * (24.09.2026, Ticket 2567, Stufe 0a — Prüfung am selben Tag)
 *
 * Entschieden über das Konto (userId), nicht nur über die Art: Ein Ticket,
 * das vor der Umdeutung angelegt wurde, heißt noch `ticket`, und eine
 * Erinnerung eines Fremden ist sein eigener Wortlaut. Eine Rückmeldung ist
 * immer fremd, auch wenn ihr Konto später Betreiber würde.
 */
export function fremderText(
  t: { kind: unknown; userId: number },
  istBetreiber: (userId: number) => boolean,
): boolean {
  return normKind(t.kind) === "rueckmeldung" || !istBetreiber(t.userId);
}

/**
 * Darf die Selbstverbesserung dieses Ticket bearbeiten — in der seriellen
 * Warteschlange, über „Von KI beheben“, die Antwort auf eine Rückfrage oder
 * als Aufgabe in Martins ticket_list_open? (24.09.2026, Ticket 2567, Stufe 0a)
 *
 * Nur eine echte Aufgabe (keine Rückmeldung, Erinnerung, kein Faden) UND kein
 * fremder Text (fremderText). Die zweite Hälfte fängt auch Tickets, die vor
 * der Umdeutung zur Rückmeldung angelegt wurden und darum noch `ticket`
 * heißen.
 */
export function selbstBearbeitbar(
  t: { kind: unknown; userId: number },
  istBetreiber: (userId: number) => boolean,
): boolean {
  return istAufgabe(normKind(t.kind)) && !fremderText(t, istBetreiber);
}
